import { Input, makeStyles, SearchBox, Tab, TabList } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import React, { FC } from 'react';
import { ICalculationOptions, IWhitePaperColumn } from '../../../model';
import { ICustomPalette } from '../../../theme/ICustomPalette';
import { SUBTOTAL_COLUMN_ID, TOTAL_COLUMN_ID } from '../../../utils/ReportUtils';

export interface ICalculationOptionChange {
    rowNumber: number;
    columnId: number;
    value: string;
}

interface IEditableCellProps {
    initialValue: string;
    onChange: (newValue: string) => void;
    ariaLabel: string;
    rowNumber: string;
    columnId: number;
}

interface IHighlightedTextProps {
    text: string;
    query: string;
    highlightColor: string;
}

interface ICalculationOptionsTablistListComponentProps {
    searchQuery: string;
    setSearchQuery: (value: string) => void;
    selectedTab: string;
    setSelectedTab: (value: string) => void;
    filteredColumns: IWhitePaperColumn[];
    filteredOptions: ICalculationOptions[];
    selectedColumn: IWhitePaperColumn | undefined;
    pendingChanges?: Map<string, ICalculationOptionChange>;
    onCellEdit?: (rowNumber: string | null, columnId: number, newValue: string) => void;
    isEditingEnabled?: boolean;
    styles: {
        container: string;
        mainContent: string;
        tabListContainer: string;
        contentArea: string;
        optionRow: string;
        optionName: string;
        optionValue: string;
        contentTitle: string;
    };
    getTabStyle: (isSelected: boolean) => React.CSSProperties;
    customPalette: ICustomPalette;
    t: (key: string) => string;
    contentAreaRef: React.RefObject<HTMLDivElement>;
}

const useSearchBoxStyles = makeStyles({
    root: {
        width: '250px',
        minWidth: '250px',
        maxWidth: '250px',
    },
});

const useEditableCellStyles = makeStyles({
    root: {
        width: 'calc(3.75rem)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    input: {
        height: 'calc(2rem)',
        textAlign: 'center',
        paddingTop: 'calc(0.25rem)',
        paddingBottom: 'calc(0.25rem)',
        paddingLeft: 'calc(0.25rem)',
        paddingRight: 'calc(0.25rem)',
    },
});

const EditableCell: FC<IEditableCellProps> = ({ initialValue, onChange, ariaLabel }) => {
    const styles = useEditableCellStyles();
    const [localValue, setLocalValue] = React.useState(initialValue);

    React.useEffect(() => {
        setLocalValue(initialValue);
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, data: { value: string }) => {
        e?.stopPropagation();
        const value = data.value || '';

        // Validate: only allow empty string or whole number in the inclusive range of 0 to 99
        if (!/^$|^[0-9]$|^[1-9][0-9]$/.test(value)) {
            return;
        }

        setLocalValue(value);
        onChange(value);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.stopPropagation();
        e.target.select();
    };

    return (
        <Input
            value={localValue}
            onChange={handleChange}
            onClick={(e) => e.stopPropagation()}
            onFocus={handleFocus}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            maxLength={2}
            className={styles.root}
            input={{ className: styles.input }}
            aria-label={ariaLabel}
        />
    );
};

const HighlightedText = React.memo<IHighlightedTextProps>(({ text, query, highlightColor }) => {
    if (!text || !query.trim()) {
        return <>{text}</>;
    }

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();

    if (!lowerText.includes(lowerQuery)) {
        return <>{text}</>;
    }

    const parts: React.ReactNode[] = [];
    let startIndex = 0;

    while (startIndex < text.length) {
        const index = lowerText.indexOf(lowerQuery, startIndex);
        if (index === -1) {
            parts.push(text.slice(startIndex));
            break;
        }

        parts.push(text.slice(startIndex, index));
        const match = text.slice(index, index + query.length);
        parts.push(
            <span key={index} style={{ backgroundColor: highlightColor }}>
                {match}
            </span>
        );
        startIndex = index + query.length;
    }

    return <>{parts}</>;
});

HighlightedText.displayName = 'HighlightedText';

export const CalculationOptionsTablistListComponent: FC<ICalculationOptionsTablistListComponentProps> = ({
    searchQuery,
    setSearchQuery,
    selectedTab,
    setSelectedTab,
    filteredColumns,
    filteredOptions,
    selectedColumn,
    pendingChanges,
    onCellEdit,
    isEditingEnabled,
    styles,
    getTabStyle,
    customPalette,
    t,
    contentAreaRef,
}) => {
    const searchBoxClasses = useSearchBoxStyles();
    const [isSearchboxFocused, { setTrue: setIsSearchboxFocusedTrue, setFalse: setIsSearchboxFocusedFalse }] = useBoolean(false);
    const searchPlaceholder = t('searchForOptionOrEntity').toString();

    const handleChange = (rowNumber: string | null, columnId: number | undefined, newValue: string) => {
        if (!rowNumber || columnId === undefined || columnId === null) return;

        if (onCellEdit) {
            onCellEdit(rowNumber, columnId, newValue);
        }
    };

    const isValueEditable = (option: ICalculationOptions, column: IWhitePaperColumn | undefined) => {
        if (column && isEditingEnabled) {
            const originalColumnId = column.originalColumnId ?? 0;
            const isInputType = option.definition === 'INPUT';
            const isTotalColumn = originalColumnId == TOTAL_COLUMN_ID || originalColumnId == SUBTOTAL_COLUMN_ID;
            const canEdit = originalColumnId > 0 && !isTotalColumn && isInputType;
            return canEdit;
        }
        return false;
    };

    return (
        <div className={styles.container} data-testid='calculationOptionsTabListTestId'>
            <SearchBox
                value={searchQuery}
                className={searchBoxClasses.root}
                placeholder={isSearchboxFocused ? '' : searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(_, data) => setSearchQuery(data.value || '')}
                onFocus={setIsSearchboxFocusedTrue}
                onBlur={setIsSearchboxFocusedFalse}
                dismiss={{ onClick: () => setSearchQuery('') }}
            />

            <div className={styles.mainContent}>
                <div className={styles.tabListContainer}>
                    <TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value as string)} vertical>
                        {filteredColumns.map((column) => {
                            if (!column.fieldName) return null;

                            const tabText = column.entityName || column.headingFirstLine || column.fieldName;
                            const isSelected = selectedTab === column.fieldName;

                            return (
                                <Tab key={column.fieldName} value={column.fieldName} style={getTabStyle(isSelected)}>
                                    <HighlightedText text={tabText} query={searchQuery} highlightColor={customPalette.yellowDark} />
                                </Tab>
                            );
                        })}
                    </TabList>
                </div>

                <div className={styles.contentArea} ref={contentAreaRef}>
                    <div className={styles.contentTitle}>
                        {selectedColumn?.entityName || selectedColumn?.headingFirstLine || selectedColumn?.fieldName}
                    </div>
                    {filteredOptions.map((option, index) => {
                        const originalValue = option.value?.[selectedTab]?.current?.toString() || '';
                        const changeKey = `${option.rowNumber}-${selectedColumn?.originalColumnId}`;
                        const pendingChange = pendingChanges?.get(changeKey);
                        const value = pendingChange ? pendingChange.value : originalValue;

                        const editable = isValueEditable(option, selectedColumn);

                        return (
                            <div key={index} className={styles.optionRow}>
                                <div className={styles.optionName}>
                                    <HighlightedText
                                        text={option.name || ''}
                                        query={searchQuery}
                                        highlightColor={customPalette.yellowDark}
                                    />
                                </div>
                                <div className={styles.optionValue}>
                                    {editable ? (
                                        <EditableCell
                                            initialValue={value}
                                            onChange={(newValue) =>
                                                handleChange(option.rowNumber || null, selectedColumn?.originalColumnId, newValue)
                                            }
                                            ariaLabel={`${option.name} - ${selectedColumn?.entityName}`}
                                            rowNumber={option.rowNumber || ''}
                                            columnId={selectedColumn?.originalColumnId ?? 0}
                                        />
                                    ) : (
                                        <div>{value}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
